// modules/AuditCheckboxGroupField.jsx
import React from "react";
import {
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Box,
  Typography,
  Alert,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const CRIT_COLORS = {
  High: "#db441f",
  Medium: "#f59c16",
  Low: "#80cc5a",
};

const normalizeAuditCrit = (ac) => ({
  High:   Array.isArray(ac?.High)   ? ac.High   : [],
  Medium: Array.isArray(ac?.Medium) ? ac.Medium : [],
  Low:    Array.isArray(ac?.Low)    ? ac.Low    : [],
});

const getOptionCritLevel = (module, option) => {
  const { High, Medium, Low } = normalizeAuditCrit(module?.auditCriticality);
  if (High.includes(option))   return "High";
  if (Medium.includes(option)) return "Medium";
  if (Low.includes(option))    return "Low";
  return null;
};

const OptionLabelWithPill = ({ label, level }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Typography component="span">{label}</Typography>
    {level && (
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: 9999,
          bgcolor: CRIT_COLORS[level],
          flex: "0 0 auto",
        }}
      />
    )}
  </Box>
);

const AuditCheckboxGroupField = ({
  module,
  value,
  isEditable,
  onChange,
  fieldKey,
}) => {
  const selected = Array.isArray(value) ? value : value ? [value] : [];

  // ── mandatory-fail detection ─────────────────────────────────────────────
  const { High } = normalizeAuditCrit(module?.auditCriticality);
  const hasMandatoryFail = High.length > 0 && selected.some((opt) => High.includes(opt));

  const handleSingle = (option, checked) => {
    onChange(fieldKey, checked ? [option] : []);
  };

  const handleMulti = (option, checked) => {
    if (checked) onChange(fieldKey, [...selected, option]);
    else onChange(fieldKey, selected.filter((v) => v !== option));
  };

  return (
    <FormControl
      component="fieldset"
      margin="normal"
      required={module.required}
      disabled={!isEditable}
      sx={{ width: "95%", mb: 2 }}
    >
      {/* Label row — weight badge if present */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <FormLabel component="legend" sx={{ flex: 1 }}>
          {module.label}
        </FormLabel>
        {typeof module.weight === "number" && module.weight > 0 && (
          <Box
            sx={{
              fontSize: "0.7rem",
              fontWeight: 600,
              color: "#fff",
              bgcolor: "#5c6bc0",
              borderRadius: 99,
              px: 1,
              py: 0.25,
              whiteSpace: "nowrap",
              lineHeight: 1.4,
            }}
          >
            {module.weight} pts
          </Box>
        )}
      </Box>

      <FormGroup
        sx={{ gap: 0.5, flexDirection: "column", alignItems: "flex-start" }}
      >
        {module.options.map((option, idx) => {
          const level = getOptionCritLevel(module, option);
          return (
            <FormControlLabel
              key={idx}
              control={
                <Checkbox
                  checked={selected.includes(option)}
                  onChange={(e) =>
                    module.multiple === false
                      ? handleSingle(option, e.target.checked)
                      : handleMulti(option, e.target.checked)
                  }
                  disabled={!isEditable}
                />
              }
              label={<OptionLabelWithPill label={option} level={level} />}
            />
          );
        })}
      </FormGroup>

      {module.required && selected.length === 0 && (
        <FormHelperText error>
          {module.multiple === false
            ? "Please select one option"
            : "Please select at least one option"}
        </FormHelperText>
      )}

      {/* ── Mandatory-fail inline alert ─────────────────────────────────── */}
      {hasMandatoryFail && (
        <Alert
          severity="error"
          icon={<WarningAmberIcon />}
          sx={{
            mt: 1,
            fontWeight: 600,
            border: "1.5px solid #d00000",
            "& .MuiAlert-message": { fontSize: "0.83rem" },
          }}
        >
          <strong>Critical Risk — Automatic FAIL</strong>
          <br />
          This answer identifies a critical risk item. The audit will be
          recorded as <strong>FAIL</strong> regardless of other responses.
        </Alert>
      )}
    </FormControl>
  );
};

export default AuditCheckboxGroupField;